import api from '../api/client';
import React from 'react';
import { Card, Row, Col } from 'antd';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const WeatherCharts = ({ forecastData, historicalData }) => {
    if (!forecastData || !historicalData) return null;

    // Temperature forecast chart data
    const tempChartData = {
        labels: forecastData.map((item) => new Date(item.datetime).getHours() + ':00'),
        datasets: [
            {
                label: 'Temperature (°C)',
                data: forecastData.map((item) => item.temperature),
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                fill: true,
                tension: 0.4
            }
        ]
    };

    const tempChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top'
            },
            title: {
                display: true,
                text: '24-Hour Temperature Forecast'
            }
        },
        scales: {
            y: {
                beginAtZero: false,
                title: {
                    display: true,
                    text: 'Temperature (°C)'
                }
            }
        }
    };

    // Humidity and precipitation chart
    const humidityChartData = {
        labels: forecastData.map((item) => new Date(item.datetime).getHours() + ':00'),
        datasets: [
            {
                label: 'Humidity (%)',
                data: forecastData.map((item) => item.humidity),
                borderColor: 'rgb(54, 162, 235)',
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                yAxisID: 'y',
                fill: true
            },
            {
                label: 'Precipitation Chance (%)',
                data: forecastData.map((item) => item.precipitation),
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                yAxisID: 'y1',
                type: 'bar'
            }
        ]
    };

    const humidityChartOptions = {
        responsive: true,
        interaction: {
            mode: 'index',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: 'Humidity & Precipitation Forecast'
            }
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Humidity (%)'
                },
                max: 100
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Precipitation (%)'
                },
                max: 100,
                grid: {
                    drawOnChartArea: false
                }
            }
        }
    };

    // Historical trends chart
    const historicalChartData = {
        labels: historicalData.map((item) => {
            const date = new Date(item.date);
            return `${date.getMonth() + 1}/${date.getDate()}`;
        }),
        datasets: [
            {
                label: 'Temperature (°C)',
                data: historicalData.map((item) => item.temperature),
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.1)',
                yAxisID: 'y'
            },
            {
                label: 'Soil Moisture (%)',
                data: historicalData.map((item) => item.soilMoisture),
                borderColor: 'rgb(139, 69, 19)',
                backgroundColor: 'rgba(139, 69, 19, 0.1)',
                yAxisID: 'y1'
            },
            {
                label: 'Crop Health (%)',
                data: historicalData.map((item) => item.cropHealth),
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.1)',
                yAxisID: 'y1'
            }
        ]
    };

    const historicalChartOptions = {
        responsive: true,
        interaction: {
            mode: 'index',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: '7-Day Historical Trends'
            }
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Temperature (°C)'
                }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Percentage (%)'
                },
                max: 100,
                grid: {
                    drawOnChartArea: false
                }
            }
        }
    };

    return (
        <Card title="Weather Analytics & Forecast" bordered={false}>
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={12}>
                    <Line data={tempChartData} options={tempChartOptions} />
                </Col>
                <Col xs={24} lg={12}>
                    <Line data={humidityChartData} options={humidityChartOptions} />
                </Col>
                <Col xs={24}>
                    <Line data={historicalChartData} options={historicalChartOptions} />
                </Col>
            </Row>
        </Card>
    );
};

export default WeatherCharts;
